import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { FundService } from './fund/fund.service';
import { CharityService, CharityResponse } from './charity/charity.service';
import { ProjectService, ProjectDetailsResponse } from './project/project.service';
import { SignatureService } from './signature/signature.service';
import { GsUserService } from './user/gs-user.service';
import { UtmAttributionService } from './utm-attribution.service';
import type { DonorLoginData } from '@gs-2.0/shared';
import { getGenericErrorMessage } from '../utils/error-message.util';
import { AppRoutes } from '../constants/routes.enum';

/**
 * Interface for card data with entity type
 */
export interface GivingCardData {
  uuid: string;
  languageCode?: string | null;
  entityType: 'charity' | 'project';
  /** Display name of the charity/organisation — pre-populates the Charity field on the Grants page */
  charityName?: string;
  /** For project entities — the project name used to pre-select the matching project on the Grants page */
  projectName?: string;
}

/**
 * Service to handle donor login and giving link redirects
 * This centralizes the logic for:
 * - Checking if user is logged in
 * - Opening donor login dialog
 * - Handling login, guest, and forgot-password flows
 * - Fetching giving links from charity/project services
 * - Handling post-login redirects
 */
@Injectable({
  providedIn: 'root'
})
export class DonorGivingService {
  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private fundService: FundService,
    private charityService: CharityService,
    private projectService: ProjectService,
    private signatureService: SignatureService,
    private router: Router,
    private gsUserService: GsUserService,
    private utmAttributionService: UtmAttributionService
  ) {}

  /**
   * Checks if user is authenticated
   */
  isUserLoggedIn(): boolean {
    return this.authService.hasValidToken();
  }

  /**
   * Checks if the logged-in user can proceed with giving/granting.
   * Returns true only if all conditions are met:
   * - Has portal access (PORTAL_USER or PORTAL_ADMIN)
   * - Has an active Foundation Fund (DAF type)
   * - Has Full Access (not View-Only)
   *
   * Gate on hasPortalAccess() rather than hasPortalUserRole(): PORTAL_ADMIN-only
   * fund holders are legitimate DAF grantors and must not be excluded here.
   */
  private canProceedWithGiving(): boolean {
    if (!this.authService.hasPortalAccess()) return false;
    return this.fundService.hasAccessibleFoundationFund();
  }

  /**
   * Fetches the giving link for a charity or project
   * Returns the giving link or null if not found
   */
  private async fetchGivingLink(cardData: GivingCardData): Promise<string | null> {
    const isCharity = cardData.entityType === 'charity';
    const languageCode = cardData.languageCode || 'eng';

    try {
      if (isCharity) {
        const response = await this.charityService.getCharity(cardData.uuid, languageCode).toPromise();
        const givingLink = response?.success && response.data?.givingLink ? response.data.givingLink : null;
        if (!givingLink) {
          console.error('[DonorGivingService] No giving link found in charity response');
        }
        return givingLink;
      } else {
        const response = await this.projectService.getProjectDetails(cardData.uuid, languageCode).toPromise();
        const givingLink = response?.success && response.data?.projectGivingLink ? response.data.projectGivingLink : null;
        if (!givingLink) {
          console.error('[DonorGivingService] No giving link found in project response');
        }
        return givingLink;
      }
    } catch (error) {
      console.error('[DonorGivingService] Error fetching giving link:', error);
      return null;
    }
  }

  /**
   * Main method to handle Give button click
   * Shows login dialog based on authentication state and portal access:
   * - Not authenticated → Show dialog
   * - Authenticated portal user (PORTAL_USER or PORTAL_ADMIN) with DAF access → Redirect immediately
   * - Authenticated without portal access → Show dialog
   * @param cardData - The card data with uuid, languageCode, and entityType
   * @param destroy$ - Subject for cleanup (from calling component)
   * @param errorMessage - Optional error message to display in dialog
   * @param directGivingLink - Optional direct giving link (bypasses API fetch)
   */
  async handleGiveClick(
    cardData: GivingCardData,
    destroy$: Subject<void>,
    errorMessage?: string,
    directGivingLink?: string
  ): Promise<void> {
    if (!this.isUserLoggedIn()) {
      // User is not authenticated - show dialog
      this.openDonorLoginDialog(cardData, destroy$, errorMessage, directGivingLink);
    } else {
      // Load funds to ensure _apiFunds is populated before checking access.
      // Skip if already populated for this session — F1's dedup would share the
      // in-flight promise here, but skipping outright avoids the wasted pre-load
      // and tightens the race window for the post-login force-refetch below.
      try {
        if (this.fundService.funds().length === 0) {
          await this.fundService.loadFunds();
        }
      } catch {
        // this.openNoAccessDialog(cardData, destroy$, directGivingLink);
        this.openGivingLinkAsGuest(cardData, directGivingLink);
        return;
      }

      // Check portal access and fund-level access conditions
      if (this.canProceedWithGiving()) {
        // User has portal access + active Foundation Fund + Full Access
        // Navigate to the Grants page with charity pre-populated
        const charityHash = directGivingLink ? this.extractCharityHash(directGivingLink) : undefined;
        this.navigateToGrantPage(cardData.charityName, charityHash, cardData.entityType, cardData.projectName);
      } else {
        // Unable to proceed — any of the following:
        // - No portal access
        // - No active Foundation Fund
        // - Only has Charity/Project Fund (no Foundation Fund)
        // - Foundation Fund with View-Only access
        // this.openNoAccessDialog(cardData, destroy$, directGivingLink);
        this.openGivingLinkAsGuest(cardData, directGivingLink);
      }
    }
  }

  /**
   * Opens the donor login dialog and handles the result
   * @param cardData - The card data
   * @param destroy$ - Subject for cleanup
   * @param errorMessage - Optional error message
   * @param directGivingLink - Optional direct giving link (bypasses API fetch)
   */
  private openDonorLoginDialog(
    cardData: GivingCardData,
    destroy$: Subject<void>,
    errorMessage?: string,
    directGivingLink?: string
  ): void {
    // Dynamically import DonorLoginComponent to avoid circular dependencies
    import('@gs-2.0/shared').then(({ DonorLoginComponent }) => {
      const dialogData: DonorLoginData = {
        errorMessage: errorMessage,
        onLogin: async (username: string, password: string) => {
          // Save original user's tokens before attempting new login
          // This allows us to restore the session if the new user doesn't have portal access
          const originalTokens = {
            access_token: localStorage.getItem('access_token'),
            id_token: localStorage.getItem('id_token'),
            refresh_token: localStorage.getItem('refresh_token'),
            nonce: localStorage.getItem('nonce'),
            PKCE_verifier: localStorage.getItem('PKCE_verifier'),
            expires_at: localStorage.getItem('expires_at'),
            id_token_claims_obj: localStorage.getItem('id_token_claims_obj'),
            id_token_expires_at: localStorage.getItem('id_token_expires_at'),
            id_token_stored_at: localStorage.getItem('id_token_stored_at'),
            access_token_stored_at: localStorage.getItem('access_token_stored_at'),
            granted_scopes: localStorage.getItem('granted_scopes'),
            session_state: localStorage.getItem('session_state')
          };

          // Clear ALL old tokens from localStorage before new login
          // This ensures loginWithPassword starts with a clean slate and doesn't get confused by old tokens
          Object.keys(originalTokens).forEach(key => {
            localStorage.removeItem(key);
          });

          // Handle password-based login
          // Skip auth state update - we'll update it manually after the portal-access check to avoid UI flicker
          const loginResult = await this.authService.loginWithPassword(username, password, true);

          if (loginResult.success) {
            // Verify tokens are stored
            const accessToken = this.authService.getAccessToken();
            const hasValidToken = this.authService.hasValidToken();

            if (!accessToken || !hasValidToken) {
              throw new Error('Login succeeded but session initialization failed. Please try again.');
            }

            // Check if the newly logged-in user has portal access (PORTAL_USER or
            // PORTAL_ADMIN). PORTAL_ADMIN-only DAF holders are legitimate grantors,
            // so we gate on portal access here, not the PORTAL_USER role specifically.
            if (!this.authService.hasPortalAccess()) {
              // New user has no portal access
              // Need to handle two cases:
              // 1. Original user was authenticated - restore their session
              // 2. Original user was unauthenticated - clear tokens and keep them logged out

              /*
              //No need for this handling since there is no expected token if
              //login modal is shown
              if (originalTokens.access_token) {
                // Original user was authenticated - restore their tokens
                Object.keys(originalTokens).forEach(key => {
                  const value = originalTokens[key as keyof typeof originalTokens];
                  if (value !== null) {
                    localStorage.setItem(key, value);
                  } else {
                    localStorage.removeItem(key);
                  }
                });
                // Note: Original user's auth state is already true, no need to update
              } else {
                // Original user was unauthenticated - clear all tokens and auth state
                this.authService.clearTokensWithoutNavigation();
              }
              */
              // Close the current dialog
              dialogRef.close();

              // this.openNoAccessDialog(cardData, destroy$, directGivingLink);
              this.openGivingLinkAsGuest(cardData, directGivingLink);
              return;
            }

            // User has portal access — check Foundation Fund access before proceeding
            dialogRef.close();

            // Clear leftover OAuth state when switching users
            if (originalTokens.access_token) {
              localStorage.removeItem('nonce');
              localStorage.removeItem('PKCE_verifier');
              localStorage.removeItem('state');
              sessionStorage.removeItem('nonce');
              sessionStorage.removeItem('PKCE_verifier');
              sessionStorage.removeItem('state');
            }

            // Update auth state to trigger reactive updates in components
            this.authService.updateAuthenticationState(true);

            // Wait for auth state to propagate
            await new Promise(resolve => setTimeout(resolve, 500));

            // User identity just changed. Wipe both caches that belong to the
            // previous user before reloading; otherwise F1's loadFunds dedup
            // could share an in-flight promise issued under the old token,
            // and F3's getUserInfo cache would serve the original user's
            // entityDto to loadOrganizationProfile on the dashboard.
            this.fundService.clearFundState();
            this.gsUserService.resetUserInfoCache();

            // Load funds so Foundation Fund access can be verified
            try {
              await this.fundService.loadFunds({ force: true });
            } catch {
              // this.openNoAccessDialog(cardData, destroy$, directGivingLink);
              this.openGivingLinkAsGuest(cardData, directGivingLink);
              return;
            }

            if (this.fundService.hasAccessibleFoundationFund()) {
              const charityHash = directGivingLink ? this.extractCharityHash(directGivingLink) : undefined;
              await this.navigateToGrantPage(cardData.charityName, charityHash, cardData.entityType, cardData.projectName);
            } else {
              // this.openNoAccessDialog(cardData, destroy$, directGivingLink);
              this.openGivingLinkAsGuest(cardData, directGivingLink);
            }
          } else {
            // Login failed - throw error to show in dialog
            const errorMsg = getGenericErrorMessage(loginResult.error);
            throw new Error(errorMsg);
          }
        }
      };

      // Show donor login dialog
      const dialogRef = this.dialog.open(DonorLoginComponent, {
        data: dialogData,
        maxWidth: '95vw',
        width: '721px',
        panelClass: 'donor-login-dialog-container',
        autoFocus: false,
        hasBackdrop: true,
        disableClose: false
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          if (result.action === 'forgot-password') {
            // Redirect to Keycloak forgot password page
            const currentUrl = window.location.href;
            this.authService.forgotPassword(currentUrl);
          } else if (result.action === 'guest') {
            // Continue as guest - redirect immediately
            if (directGivingLink) {
              this.redirectToGivingLink(directGivingLink, false);
            } else {
              this.fetchGivingLinkAndRedirect(cardData, false, destroy$);
            }
          }
        }
        // If result is null/undefined, user closed dialog - do nothing
      });
    });
  }

  /**
   * Opens the "Unable to Proceed" dialog for users without granting access
   * @param cardData - The card data
   * @param destroy$ - Subject for cleanup
   * @param directGivingLink - Optional direct giving link
   */
  private openNoAccessDialog(
    cardData: GivingCardData,
    destroy$: Subject<void>,
    directGivingLink?: string
  ): void {
    const showDialog = (resolvedLink?: string) => {
      // Dynamically import UnableToProceedDialogComponent to avoid circular dependencies
      import('@gs-2.0/shared').then(({ UnableToProceedDialogComponent }) => {
        const dialogRef = this.dialog.open(UnableToProceedDialogComponent, {
          data: {},
          maxWidth: '95vw',
          width: '549px',
          panelClass: 'unable-to-proceed-dialog-container',
          autoFocus: false,
          hasBackdrop: true,
          disableClose: false
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result?.action === 'guest' && resolvedLink) {
            this.authService.clearTokensWithoutNavigation();
            this.redirectToGivingLink(resolvedLink, false);
          }
        });
      });
    };

    if (directGivingLink) {
      // Link already known — original behavior preserved
      showDialog(directGivingLink);
    } else {
      // No direct link — fetch first; if found pass to dialog, otherwise show dialog without a link
      this.fetchGivingLink(cardData).then(givingLink => {
        showDialog(givingLink ?? undefined);
      });
    }
  }

  private openGivingLinkAsGuest(cardData: GivingCardData, directGivingLink?: string): void {
    if (directGivingLink) {
      this.openGivingLink(directGivingLink, true);
    } else {
      this.fetchGivingLink(cardData).then(givingLink => {
        if (givingLink) this.openGivingLink(givingLink, true);
      });
    }
  }

  /**
   * Extracts the `customLink` hash from a giving link URL.
   * Returns undefined if the URL is invalid or the param is absent.
   */
  private extractCharityHash(url: string): string | undefined {
    try {
      return new URL(url).searchParams.get('customLink') ?? undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Navigates to the Grants page, pre-selecting the first active Foundation Fund
   * and optionally pre-populating the Charity/Project field via query params.
   * @param name - Optional charity/organisation display name — pre-populates the Charity field
   * @param charityHash - Optional `customLink` hash from the giving link URL (preferred — resolves to exact fund number)
   * @param entityType - Whether the source entity is a charity or a project
   * @param projectName - Optional project name — pre-selects the matching project on the Grants page
   *
   * Note: not `private` — `authenticatedRedirectGuard` calls this to redirect DAF users
   * who land on `/public/donate` via a give-link QR code straight to the Grants page.
   */
  async navigateToGrantPage(name?: string, charityHash?: string, entityType?: 'charity' | 'project', projectName?: string): Promise<void> {
    // Pre-select the first active Foundation Fund so the grant form opens on it
    const firstFundId = this.fundService.getFirstActiveFoundationFundId();
    if (firstFundId) {
      this.fundService.setSelectedFund(firstFundId, 'system');
    }

    const queryParams: { charityName?: string; lockCharity?: string; charityHash?: string; projectName?: string } = { lockCharity: 'true' };
    // For charities, pass the hash for precise fund resolution.
    // For projects, the giving link hash resolves to the project's own fund (not the parent charity),
    // so we skip it and rely on charityName (organisationName) to find the parent charity instead.
    if (charityHash && entityType !== 'project') {
      queryParams.charityHash = charityHash;
    }
    if (name) {
      queryParams.charityName = name;
    }
    if (projectName) {
      queryParams.projectName = projectName;
    }

    // Build the full URL so utm_source and signature can be appended consistently
    // with how giving links are signed before external redirects.
    const urlTree = this.router.createUrlTree([AppRoutes.FUND_GRANT], { queryParams });
    const fullUrl = window.location.origin + this.router.serializeUrl(urlTree);
    const signedUrl = await this.buildSignedUrl(fullUrl);

    // Extract all params from the signed URL (includes original params + utm_source + signature)
    const parsedUrl = new URL(signedUrl);
    const signedParams: Record<string, string> = {};
    parsedUrl.searchParams.forEach((value, key) => { signedParams[key] = value; });

    this.router.navigate([AppRoutes.FUND_GRANT], { queryParams: signedParams });
  }

  /**
   * Fetches the charity/project giving link and redirects to it
   * @param cardData - The card data
   * @param reloadAfterFetch - If true, stores link and reloads page (for post-login). If false, redirects immediately
   * @param destroy$ - Subject for cleanup
   */
  private fetchGivingLinkAndRedirect(
    cardData: GivingCardData,
    reloadAfterFetch: boolean,
    destroy$: Subject<void>
  ): void {
    const isCharity = cardData.entityType === 'charity';
    const languageCode = cardData.languageCode || 'eng';

    if (isCharity) {
      // Fetch charity giving link
      this.charityService.getCharity(cardData.uuid, languageCode)
        .pipe(takeUntil(destroy$))
        .subscribe({
          next: (response: CharityResponse) => {
            if (response.success && response.data?.givingLink) {
              this.redirectToGivingLink(response.data.givingLink, reloadAfterFetch);
            } else {
              console.error('No giving link available for this charity');
            }
          },
          error: (error: any) => {
            console.error('Error fetching charity details:', error);
          }
        });
    } else {
      // Fetch project giving link
      this.projectService.getProjectDetails(cardData.uuid, languageCode)
        .pipe(takeUntil(destroy$))
        .subscribe({
          next: (response: ProjectDetailsResponse) => {
            if (response.success && response.data?.projectGivingLink) {
              this.redirectToGivingLink(response.data.projectGivingLink, reloadAfterFetch);
            } else {
              console.error('No giving link available for this project');
            }
          },
          error: (error: any) => {
            console.error('Error fetching project details:', error);
          }
        });
    }
  }

  /**
   * Handles the redirect to giving link
   * @param givingLink - The giving link URL
   * @param reloadAfterFetch - If true, stores link and reloads (for post-login)
   */
  private redirectToGivingLink(givingLink: string, reloadAfterFetch: boolean): void {
    if (reloadAfterFetch) {
      // After login - store link and reload to initialize app
      sessionStorage.setItem('gs_pending_redirect', givingLink);
      window.location.reload();
    } else {
      // Guest or already logged in - redirect immediately
      this.openGivingLink(givingLink);
    }
  }

  /**
   * Resolves the `utm_source` to stamp on giving links.
   *
   * Defaults to `GivingSpace`, but when the visitor landed via a media-kit QR
   * link the attribution (`utm_source=media_kit`) is persisted in
   * `sessionStorage` for the browser session by `UtmAttributionService`. In that
   * case we honor the media-kit origin so the donation stays attributable to the
   * promotional tools even after the URL params drop during in-app navigation.
   *
   * Note: the persisted `signature` is NOT reused here — it was signed for the QR
   * landing URL and would be invalid for the giving link. `buildSignedUrl`
   * regenerates a valid signature over the resolved (media_kit) giving URL.
   */
  private resolveUtmSource(): string {
    const attribution = this.utmAttributionService.getAttribution();
    return attribution?.utm_source === 'media_kit' ? 'media_kit' : 'GivingSpace';
  }

  /**
   * Appends the resolved utm_source (media_kit when persisted, else GivingSpace)
   * and a server-generated HMAC-SHA256 signature to the giving link URL. The
   * signature is based on the full URL including utm_source but excluding the
   * signature parameter itself.
   * If signature generation fails, the URL is still returned with utm_source
   * so that the link remains accessible and attributable.
   */
  private async buildSignedUrl(url: string): Promise<string> {
    const utmSource = this.resolveUtmSource();
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', utmSource);
      const urlWithUtm = urlObj.toString();

      const response = await this.signatureService.getSignature({ data: urlWithUtm }).toPromise();
      if (response?.signature) {
        urlObj.searchParams.set('signature', response.signature);
        return urlObj.toString();
      }
      return urlWithUtm;
    } catch {
      try {
        const urlObj = new URL(url);
        urlObj.searchParams.set('utm_source', utmSource);
        return urlObj.toString();
      } catch {
        return url;
      }
    }
  }

  /**
   * Opens a giving link — in a new tab if the URL is external, same tab otherwise.
   * Appends utm_source and signature parameters before navigating.
   */
  private async openGivingLink(url: string, newTab: boolean = false): Promise<void> {
    try {
      const parsedUrl = new URL(url);
      const isExternal = parsedUrl.origin !== window.location.origin;
      if (isExternal || newTab) {
        parsedUrl.searchParams.set('giveAsGuest', 'true');
        const signedUrl = await this.buildSignedUrl(parsedUrl.toString());
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      } else {
        const signedUrl = await this.buildSignedUrl(url);
        window.location.href = signedUrl;
      }
    } catch {
      const signedUrl = await this.buildSignedUrl(url);
      window.location.href = signedUrl;
    }
  }

  /**
   * Checks for pending redirect after login and handles it
   * Should be called in component ngOnInit
   * @returns true if redirect is pending (component should return early), false otherwise
   */
  checkAndHandlePendingRedirect(): boolean {
    const pendingRedirect = sessionStorage.getItem('gs_pending_redirect');

    if (pendingRedirect && this.authService.hasValidToken()) {
      // Verify we can extract user info
      const userName = this.authService.userFullName || this.authService.userName;
      const userEmail = this.authService.userEmail;

      // We need at least a name or email to consider the user data valid
      if (userName || userEmail) {
        // Clear the pending redirect
        sessionStorage.removeItem('gs_pending_redirect');

        // Wait for app initialization to complete
        // This ensures header component and other services have initialized
        setTimeout(() => {
          this.openGivingLink(pendingRedirect);
        }, 1000);

        // Return true to signal component should return early
        return true;
      } else {
        sessionStorage.removeItem('gs_pending_redirect');
      }
    }

    return false;
  }

}
